// =====================================================================================
// INVRT Radar — web console — one site — Install tab
//
// The install snippet, the per-platform guides and the "check the pixel now" probe. Moved
// verbatim out of SiteDetailPage.tsx in the C1 split; the page shell registers it under
// the tab id "install".
//
// C1 §4: once a radar_banner site has published its banner, the server renders the LOADER
// snippet into every guide (snippet and verify_hint arrive ready to show, and are shown
// verbatim). This panel adds one line saying so, read from the same consent settings query
// the Consent tab uses.
// =====================================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardCopy, RefreshCw } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { ErrorState, LoadingState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import type { InstallGuideResponse, Site, SiteVerifyResponse } from "@/lib/types";
import { useConsentSettings } from "./consent/useConsent";

const PLATFORM_LABELS: Record<string, string> = {
  script_tag: "Raw script tag",
  google_tag_manager: "Google Tag Manager",
  wordpress: "WordPress",
  webflow: "Webflow",
  shopify: "Shopify",
  squarespace: "Squarespace",
  godaddy: "GoDaddy Website Builder",
  github_pages: "GitHub Pages",
  cloudflare: "Cloudflare",
};

export function InstallPanel({ site }: { site: Site }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId, activeMembership } = useSession();
  const { push } = useToast();
  const [platform, setPlatform] = useState<string>("script_tag");
  const [copied, setCopied] = useState(false);

  const guides = useQuery({
    queryKey: ["install", orgId, site.id],
    queryFn: () => api.get<InstallGuideResponse>(`/sites/${site.id}/install`),
    enabled: Boolean(orgId),
  });

  // The snippet below is the loader exactly when the server says so: radar_banner mode AND
  // a published banner (routes/sites.ts's usesLoaderSnippet). Secondary to the guides: a
  // slow or failed read here never blocks the snippet, it only withholds the note.
  const consent = useConsentSettings(site.id);
  const bannerIsLive =
    consent.data?.mode === "radar_banner" && consent.data.published_at !== null;

  const verify = useMutation({
    mutationFn: () => api.post<SiteVerifyResponse>(`/sites/${site.id}/verify`),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["site", orgId, site.id] });
      void queryClient.invalidateQueries({ queryKey: ["sites", orgId] });
      push({
        tone: result.pixel_status === "pending" ? "warning" : "success",
        title:
          result.pixel_status === "pending"
            ? "Radar could not find the pixel yet"
            : "Pixel confirmed",
        body: result.events_recorded
          ? "Real visitor events have arrived — this site is live."
          : result.script_detected
            ? "The snippet is on the page. Events appear once someone visits."
            : "Check the snippet is on every page you want covered, then probe again.",
      });
    },
  });

  const active = useMemo(
    () => guides.data?.guides.find((guide) => guide.platform === platform),
    [guides.data, platform],
  );

  async function copySnippet(): Promise<void> {
    if (!active) return;
    try {
      await navigator.clipboard.writeText(active.snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      push({
        tone: "warning",
        title: "Copying was blocked",
        body: "Your browser refused clipboard access. Select the snippet and copy it manually.",
      });
    }
  }

  if (guides.isLoading) return <LoadingState label="Loading the install guide…" />;
  if (guides.isError) {
    return <ErrorState error={guides.error} onRetry={() => void guides.refetch()} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Install the pixel</CardTitle>
          {activeMembership?.is_org_admin ? (
            <Button
              variant="secondary"
              size="sm"
              loading={verify.isPending}
              onClick={() => verify.mutate()}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Check the pixel now
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {bannerIsLive ? (
            <p className="rounded-lg border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-700">
              Your banner is live. The snippet below loads it and asks for consent before
              anything else runs.
            </p>
          ) : null}
          {guides.data?.instruction ? (
            <p className="text-sm text-ink-muted">{guides.data.instruction}</p>
          ) : null}

          <Select
            label="Where does your site run?"
            value={platform}
            onChange={(event) => setPlatform(event.target.value)}
            className="max-w-sm"
          >
            {(guides.data?.guides ?? []).map((guide) => (
              <option key={guide.platform} value={guide.platform}>
                {PLATFORM_LABELS[guide.platform] ?? guide.platform}
              </option>
            ))}
          </Select>

          {active ? (
            <>
              <ol className="list-inside list-decimal space-y-1 text-sm text-ink-muted">
                {active.steps.map((step) => (
                  <li key={step}>{step}</li>
                ))}
              </ol>

              <div className="relative">
                <pre className="overflow-x-auto rounded-lg bg-ink px-4 py-3 text-xs text-white">
                  <code>{active.snippet}</code>
                </pre>
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute right-2 top-2"
                  onClick={() => void copySnippet()}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  ) : (
                    <ClipboardCopy className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {copied ? "Copied" : "Copy snippet"}
                </Button>
              </div>

              {active.verify_hint ? (
                <p className="text-xs text-ink-muted">{active.verify_hint}</p>
              ) : null}
              {active.careers_page_exclusion_note ? (
                <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-warning">
                  {active.careers_page_exclusion_note}
                </p>
              ) : null}
              {active.automation?.description ? (
                <div className="rounded-lg border border-line bg-surface-subtle px-3 py-2 text-xs text-ink-muted">
                  <p className="font-medium text-ink">Install it programmatically</p>
                  <p className="mt-1">{active.automation.description}</p>
                  {active.automation.api_hint ? (
                    <p className="mt-1 font-mono">{active.automation.api_hint}</p>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}

          {verify.isError ? <ErrorState error={verify.error} /> : null}
        </CardContent>
      </Card>
    </div>
  );
}
